import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Select,
  Input,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Spinner,
  Badge,
  Divider,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Code,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardHeader,
  CardBody,
  Flex,
  Icon,
  Link
} from '@chakra-ui/react';
import { WarningIcon, CheckIcon, InfoIcon, DownloadIcon, AttachmentIcon } from '@chakra-ui/icons';
import { useDropzone } from 'react-dropzone';
import { useUser, useGlobalAction, useAction } from '@gadgetinc/react';
import { api } from '../api';
import { normalizeRoles } from '../lib/roleUtils';
import VectorStoreCreationModal from '../components/VectorStoreCreationModal';

const STEPS = {
  PARAMETERS: 'parameters',
  VECTOR_STORE: 'vector_store',
  CONFIRMATION: 'confirmation',
  GENERATION: 'generation',
  QUESTIONS: 'questions',
  RESULTS: 'results'
};

const TECHNICAL_LEVELS = [
  { value: 'beginner', label: 'Beginner - I need detailed explanations and step-by-step guidance' },
  { value: 'intermediate', label: 'Intermediate - I understand basic concepts but need some guidance' },
  { value: 'advanced', label: 'Advanced - I can handle complex technical details and implementation' }
];

const EXAMPLE_GOALS = [
  "Create a patient documentation system that automatically extracts key clinical data from handwritten notes",
  "Build an AI tutor for emergency medicine students that adapts to their learning pace",
  "Develop a real-time clinical decision support system for paramedic field operations",
  "Design an automated quality assurance system for clinical documentation compliance"
];

export default function AISystemGenerator() {
  const navigate = useNavigate();
  const user = useUser();
  const toast = useToast();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  
  // Form state
  const [currentStep, setCurrentStep] = useState(STEPS.PARAMETERS);
  const [formData, setFormData] = useState({
    userGoal: '',
    userTechnicalLevel: 'intermediate',
    institution: '',
    systemName: '',
    vectorStoreId: null
  });
  
  // File upload and vector store state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isVectorStoreModalOpen, setIsVectorStoreModalOpen] = useState(false);
  const [vectorStoreCreationProgress, setVectorStoreCreationProgress] = useState(0);
  const [isCreatingVectorStore, setIsCreatingVectorStore] = useState(false);
  const [vectorStoreError, setVectorStoreError] = useState(null);
  
  // Generation state
  const [generationHistory, setGenerationHistory] = useState([]);
  const [currentIteration, setCurrentIteration] = useState(0);
  const [tokenUsage, setTokenUsage] = useState({ used: 0, estimated: 0 });
  const [errorHistory, setErrorHistory] = useState([]);
  const [clarifyingQuestions, setClarifyingQuestions] = useState(null);
  const [questionResponses, setQuestionResponses] = useState({});
  const [currentPhase, setCurrentPhase] = useState('');
  const [progress, setProgress] = useState(0);
  
  // API integration
  const [{ data, error, fetching }, generateAIDataSystem] = useGlobalAction(api.generateAIDataSystem);
  const [{ data: vectorStoreData, error: vectorStoreCreateError, fetching: vectorStoreFetching }, createVectorStoreWithFiles] = useGlobalAction(api.createVectorStoreWithFiles);

  // Access control
  useEffect(() => {
    if (user) {
      const roles = normalizeRoles(user.roles || []);
      const hasAdminAccess = roles.some(role => 
        role === 'admin' || role === 'system-admin' || 
        role?.key === 'admin' || role?.key === 'system-admin'
      );
      
      if (!hasAdminAccess) {
        toast({
          title: 'Access Denied',
          description: 'You do not have permission to access the AI System Generator. This tool requires admin privileges.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/');
      }
    }
  }, [user, navigate, toast]);

  // Auto-populate institution
  useEffect(() => {
    if (user?.institution) {
      setFormData(prev => ({
        ...prev,
        institution: user.institution
      }));
    }
  }, [user]);

  // Handle vector store creation result
  useEffect(() => {
    if (vectorStoreData && vectorStoreData.success && vectorStoreData.result) {
      setFormData(prev => ({
        ...prev,
        vectorStoreId: vectorStoreData.result.id || vectorStoreData.result.vectorStoreId
      }));
      setIsCreatingVectorStore(false);
      setVectorStoreCreationProgress(100);
      toast({
        title: 'Vector Store Created',
        description: 'Your knowledge base has been created successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setCurrentStep(STEPS.CONFIRMATION);
    } else if (vectorStoreData && vectorStoreData.success === false) {
      setIsCreatingVectorStore(false);
      const errorMessage = vectorStoreData.errors?.join(', ') || 'Failed to create vector store';
      setVectorStoreError(errorMessage);
      toast({
        title: 'Vector Store Creation Failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [vectorStoreData, toast]);

  // Handle vector store creation error
  useEffect(() => {
    if (vectorStoreCreateError) {
      setIsCreatingVectorStore(false);
      setVectorStoreError(vectorStoreCreateError.message);
      toast({
        title: 'Vector Store Creation Failed',
        description: vectorStoreCreateError.message || 'Failed to create knowledge base',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [vectorStoreCreateError, toast]);

  // Handle generation results
  useEffect(() => {
    if (data) {
      // Update token usage
      if (data.totalTokensUsed !== undefined) {
        setTokenUsage(prev => ({ ...prev, used: data.totalTokensUsed }));
      } else if (data.tokensUsed !== undefined) {
        setTokenUsage(prev => ({ ...prev, used: prev.used + data.tokensUsed }));
      }

      // Update progress and phase
      if (data.progress !== undefined) {
        setProgress(data.progress);
      }
      if (data.phase) {
        setCurrentPhase(data.phase);
      }

      // Handle different response types
      switch (data.responseType) {
        case 'questions':
          setClarifyingQuestions(data.questions);
          setCurrentStep(STEPS.QUESTIONS);
          toast({
            title: 'Clarification Needed',
            description: 'Please answer the following questions to continue.',
            status: 'info',
            duration: 3000,
            isClosable: true,
          });
          break;

        case 'partial_result':
          // Just update progress and continue waiting
          toast({
            title: `Phase ${data.phase} Complete`,
            description: 'Generation continuing...',
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
          break;

        case 'final_result':
          setCurrentStep(STEPS.RESULTS);
          setGenerationHistory(prev => [...prev, {
            timestamp: new Date().toISOString(),
            result: data,
            success: true
          }]);
          toast({
            title: 'System Generated Successfully',
            description: 'Your AI system specifications have been generated.',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
          break;

        case 'error':
          setErrorHistory(prev => [...prev, {
            timestamp: new Date().toISOString(),
            error: data.error || 'Unknown error occurred',
            iteration: currentIteration,
            phase: data.phase
          }]);
          toast({
            title: 'Generation Error',
            description: data.error || 'An error occurred during generation.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          break;

        default:
          // Handle legacy format or unknown response type
          if (data.success === false) {
            setErrorHistory(prev => [...prev, {
              timestamp: new Date().toISOString(),
              error: data.errors?.join(', ') || 'Unknown error occurred',
              iteration: currentIteration
            }]);
          }
          break;
      }
    }
  }, [data, currentIteration, toast]);

  // Handle errors
  useEffect(() => {
    if (error) {
      setErrorHistory(prev => [...prev, {
        timestamp: new Date().toISOString(),
        error: error.message || 'Network error occurred',
        iteration: currentIteration
      }]);
      toast({
        title: 'Generation Failed',
        description: error.message || 'An error occurred during system generation.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [error, currentIteration, toast]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.userGoal.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a user goal for your AI system.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    if (!formData.systemName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a system name.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const handleFileUpload = (files) => {
    setUploadedFiles(files);
  };

  const handleCreateVectorStore = async () => {
    if (uploadedFiles.length === 0) {
      toast({
        title: 'No Files Selected',
        description: 'Please upload some files first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCreatingVectorStore(true);
    setVectorStoreCreationProgress(0);
    setVectorStoreError(null);

    try {
      // Convert files to base64 format expected by the global action
      const convertedFiles = await Promise.all(
        uploadedFiles.map(async (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64Content = reader.result.split(',')[1]; // Remove data:type;base64, prefix
              resolve({
                name: file.name,
                content: base64Content,
                mimeType: file.type
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );

      // Create vector store with uploaded files using global action
      await createVectorStoreWithFiles({
        name: `${formData.systemName} Knowledge Base`,
        description: `Knowledge base for ${formData.systemName} AI system`,
        institution: formData.institution,
        files: convertedFiles,
        agentTypes: [],
        chunkSize: 1000,
        chunkOverlap: 200
      });
    } catch (error) {
      setIsCreatingVectorStore(false);
      setVectorStoreError(error.message);
      toast({
        title: 'Vector Store Creation Failed',
        description: error.message || 'Failed to create knowledge base',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSkipVectorStore = () => {
    setCurrentStep(STEPS.CONFIRMATION);
  };

  const handleConfirmGeneration = async () => {
    onConfirmClose();
    setCurrentStep(STEPS.GENERATION);
    setCurrentIteration(prev => prev + 1);
    
    try {
      await generateAIDataSystem({
        ...formData,
        vectorStoreId: formData.vectorStoreId
      });
    } catch (err) {
      console.error('Generation error:', err);
    }
  };

  const handleStartGeneration = () => {
    if (!validateForm()) return;
    setCurrentStep(STEPS.VECTOR_STORE);
  };

  const downloadSpecifications = () => {
    if (!data || data.responseType !== 'final_result') return;
    
    const specifications = {
      userVisible: data.userVisible,
      specification: data.specification,
      implementation: data.implementation,
      metadata: {
        systemName: formData.systemName,
        generatedAt: new Date().toISOString(),
        tokensUsed: data.totalTokensUsed
      }
    };
    
    const blob = new Blob([JSON.stringify(specifications, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.systemName.replace(/\s+/g, '_')}_specifications.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setCurrentStep(STEPS.PARAMETERS);
    setFormData({
      userGoal: '',
      userTechnicalLevel: 'intermediate',
      institution: user?.institution || '',
      systemName: '',
      vectorStoreId: null
    });
    setErrorHistory([]);
    setCurrentIteration(0);
    setClarifyingQuestions(null);
    setQuestionResponses({});
    setCurrentPhase('');
    setProgress(0);
    setUploadedFiles([]);
    setIsCreatingVectorStore(false);
    setVectorStoreCreationProgress(0);
    setVectorStoreError(null);
  };

  // Show loading while checking access
  if (!user) {
    return (
      <Container maxW="container.md" py={8}>
        <VStack spacing={6}>
          <Spinner size="xl" />
          <Text>Checking permissions...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box>
          <HStack spacing={4} mb={4}>
            <Heading size="lg">AI System Generator</Heading>
            <Badge colorScheme="red" fontSize="sm">ADMIN ONLY</Badge>
          </HStack>
          
          <Alert status="warning" mb={6}>
            <AlertIcon />
            <Box>
              <AlertTitle>Powerful System Generation Tool</AlertTitle>
              <AlertDescription>
                This tool generates complete AI system specifications and code. Use with caution as it
                consumes significant API tokens and creates production-ready systems. For issues, contact
                the development team immediately.
              </AlertDescription>
            </Box>
          </Alert>
        </Box>

        {/* Progress Indicator */}
        <Box>
          <Text fontSize="sm" color="gray.600" mb={2}>Generation Progress</Text>
          <HStack spacing={4}>
            <Badge colorScheme={currentStep === STEPS.PARAMETERS ? 'blue' : 'gray'}>1. Parameters</Badge>
            <Badge colorScheme={currentStep === STEPS.VECTOR_STORE ? 'blue' : 'gray'}>2. Knowledge Base</Badge>
            <Badge colorScheme={currentStep === STEPS.CONFIRMATION ? 'blue' : 'gray'}>3. Confirmation</Badge>
            <Badge colorScheme={currentStep === STEPS.GENERATION ? 'blue' : 'gray'}>4. Generation</Badge>
            <Badge colorScheme={currentStep === STEPS.QUESTIONS ? 'orange' : 'gray'}>5. Questions</Badge>
            <Badge colorScheme={currentStep === STEPS.RESULTS ? 'green' : 'gray'}>6. Results</Badge>
          </HStack>
          {currentPhase && (
            <Text fontSize="xs" color="gray.500" mt={1}>Current phase: {currentPhase}</Text>
          )}
          {progress > 0 && (
            <Progress value={progress} colorScheme="blue" size="sm" mt={2} />
          )}
        </Box>

        {/* Status Dashboard */}
        {(currentIteration > 0 || errorHistory.length > 0) && (
          <Card>
            <CardHeader>
              <Heading size="md">Generation Status</Heading>
            </CardHeader>
            <CardBody>
              <HStack spacing={8}>
                <Stat>
                  <StatLabel>Current Iteration</StatLabel>
                  <StatNumber>{currentIteration}</StatNumber>
                  <StatHelpText>Generation attempts</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Errors</StatLabel>
                  <StatNumber color={errorHistory.length > 0 ? 'red.500' : 'green.500'}>
                    {errorHistory.length}
                  </StatNumber>
                  <StatHelpText>Failed attempts</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Token Usage</StatLabel>
                  <StatNumber>{tokenUsage.used}</StatNumber>
                  <StatHelpText>Estimated cost: ${(tokenUsage.used * 0.002).toFixed(4)}</StatHelpText>
                </Stat>
              </HStack>
            </CardBody>
          </Card>
        )}

        {/* Main Content */}
        {currentStep === STEPS.PARAMETERS && (
          <Card>
            <CardHeader>
              <Heading size="md">System Parameters</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <FormControl isRequired>
                  <FormLabel>User Goal</FormLabel>
                  <Textarea
                    placeholder="Describe what you want your AI system to accomplish..."
                    value={formData.userGoal}
                    onChange={(e) => handleFormChange('userGoal', e.target.value)}
                    rows={6}
                  />
                  <Text fontSize="sm" color="gray.600" mt={2}>
                    Examples:
                  </Text>
                  <VStack spacing={1} align="start" pl={4}>
                    {EXAMPLE_GOALS.map((example, index) => (
                      <Text key={index} fontSize="sm" color="blue.600" cursor="pointer"
                            onClick={() => handleFormChange('userGoal', example)}>
                        • {example}
                      </Text>
                    ))}
                  </VStack>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Technical Level</FormLabel>
                  <Select
                    value={formData.userTechnicalLevel}
                    onChange={(e) => handleFormChange('userTechnicalLevel', e.target.value)}
                  >
                    {TECHNICAL_LEVELS.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Institution</FormLabel>
                  <Input
                    value={formData.institution}
                    onChange={(e) => handleFormChange('institution', e.target.value)}
                    placeholder="Enter your institution name"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>System Name</FormLabel>
                  <Input
                    value={formData.systemName}
                    onChange={(e) => handleFormChange('systemName', e.target.value)}
                    placeholder="Enter a name for your AI system"
                  />
                </FormControl>

                <Button colorScheme="blue" size="lg" onClick={handleStartGeneration}>
                  Continue to Knowledge Base Setup
                </Button>
              </VStack>
            </CardBody>
          </Card>
        )}

        {currentStep === STEPS.VECTOR_STORE && (
          <Card>
            <CardHeader>
              <Heading size="md">Knowledge Base Setup (Optional)</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Optional: Upload Industry-Specific Documents</AlertTitle>
                    <AlertDescription>
                      This step is completely optional. Upload documents only if you have industry-specific 
                      knowledge, guidelines, or documentation that would help create a more accurate AI system. 
                      The system can generate effectively without uploaded documents - feel free to skip if 
                      you don't have relevant files readily available.
                    </AlertDescription>
                  </Box>
                </Alert>

                <Box p={4} bg="blue.50" borderRadius="md">
                  <VStack spacing={3} align="start">
                    <Text fontWeight="bold" color="blue.800">Recommended File Types:</Text>
                    <Text fontSize="sm" color="blue.700">• Industry guidelines and standards</Text>
                    <Text fontSize="sm" color="blue.700">• Documentation and manuals</Text>
                    <Text fontSize="sm" color="blue.700">• Examples and case studies</Text>
                    <Text fontSize="sm" color="blue.700">• Best practices documents</Text>
                    <Text fontSize="sm" color="blue.700">• Training materials</Text>
                    <Text fontSize="sm" color="blue.700">• Technical specifications</Text>
                  </VStack>
                </Box>

                <FormControl>
                  <FormLabel>Upload Files</FormLabel>
                  <Box
                    border="2px dashed"
                    borderColor="gray.300"
                    borderRadius="md"
                    p={8}
                    textAlign="center"
                    cursor="pointer"
                    _hover={{ borderColor: "blue.500", bg: "blue.50" }}
                    onClick={() => setIsVectorStoreModalOpen(true)}
                  >
                    <VStack spacing={2}>
                      <Icon as={AttachmentIcon} w={8} h={8} color="gray.500" />
                      <Text color="gray.600">
                        Click here to upload files and create knowledge base
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {uploadedFiles.length > 0 
                          ? `${uploadedFiles.length} file(s) selected`
                          : "No files selected"
                        }
                      </Text>
                    </VStack>
                  </Box>
                </FormControl>

                {uploadedFiles.length > 0 && (
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <Text fontWeight="bold" mb={2}>Selected Files:</Text>
                    <VStack spacing={1} align="start">
                      {uploadedFiles.map((file, index) => (
                        <Text key={index} fontSize="sm" color="gray.700">
                          • {file.name}
                        </Text>
                      ))}
                    </VStack>
                  </Box>
                )}

                {isCreatingVectorStore && (
                  <Alert status="info">
                    <AlertIcon />
                    <Box flex="1">
                      <AlertTitle>Creating Knowledge Base...</AlertTitle>
                      <AlertDescription>
                        Processing your files and creating the knowledge base. This may take a few minutes.
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}

                {vectorStoreCreationProgress > 0 && vectorStoreCreationProgress < 100 && (
                  <Progress value={vectorStoreCreationProgress} colorScheme="blue" />
                )}

                {vectorStoreError && (
                  <Alert status="error">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Vector Store Creation Failed</AlertTitle>
                      <AlertDescription>{vectorStoreError}</AlertDescription>
                    </Box>
                  </Alert>
                )}

                <HStack spacing={4}>
                  <Button 
                    onClick={() => setCurrentStep(STEPS.PARAMETERS)}
                    variant="outline"
                  >
                    Back to Parameters
                  </Button>
                  <Button 
                    onClick={handleSkipVectorStore}
                    variant="outline"
                  >
                    Skip Knowledge Base
                  </Button>
                  <Button 
                    colorScheme="blue" 
                    onClick={handleCreateVectorStore}
                    isDisabled={uploadedFiles.length === 0 || isCreatingVectorStore}
                    isLoading={isCreatingVectorStore}
                    loadingText="Creating..."
                  >
                    Create Knowledge Base
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        )}

        {currentStep === STEPS.CONFIRMATION && (
          <Card>
            <CardHeader>
              <Heading size="md">Confirm System Generation</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Review Your Specifications</AlertTitle>
                    <AlertDescription>
                      Please review your system parameters before generation. This process may take several
                      minutes and will consume API tokens.
                    </AlertDescription>
                  </Box>
                </Alert>

                <Box p={4} bg="gray.50" borderRadius="md">
                  <VStack spacing={3} align="start">
                    <Text color="gray.800"><strong>Goal:</strong> {formData.userGoal}</Text>
                    <Text color="gray.800"><strong>Technical Level:</strong> {formData.userTechnicalLevel}</Text>
                    <Text color="gray.800"><strong>Institution:</strong> {formData.institution}</Text>
                    <Text color="gray.800"><strong>System Name:</strong> {formData.systemName}</Text>
                    <Text color="gray.800">
                      <strong>Knowledge Base:</strong> {formData.vectorStoreId ? 'Created successfully' : 'None created'}
                    </Text>
                  </VStack>
                </Box>

                <Alert status="warning">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Cost Warning</AlertTitle>
                    <AlertDescription>
                      System generation may consume 1000-5000 API tokens (approximately $2-10).
                      Are you sure you want to proceed?
                    </AlertDescription>
                  </Box>
                </Alert>

                <HStack spacing={4}>
                  <Button 
                    onClick={() => setCurrentStep(STEPS.VECTOR_STORE)}
                    variant="outline"
                  >
                    Back to Knowledge Base
                  </Button>
                  <Button colorScheme="red" onClick={onConfirmOpen}>
                    Confirm & Generate System
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        )}

        {currentStep === STEPS.GENERATION && (
          <Card>
            <CardHeader>
              <HStack justifyContent="space-between">
                <Heading size="md">Generating AI System...</Heading>
                <HStack spacing={2}>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    colorScheme="gray"
                    onClick={() => {
                      setCurrentStep(STEPS.PARAMETERS);
                      toast({
                        title: 'Generation Cancelled',
                        description: 'Returning to parameters screen.',
                        status: 'info',
                        duration: 3000,
                        isClosable: true,
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="sm" 
                    colorScheme="red" 
                    onClick={resetForm}
                  >
                    Reset All
                  </Button>
                </HStack>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <HStack>
                  <Spinner />
                  <VStack align="start" spacing={0}>
                    <Text>Generation in progress (Iteration {currentIteration})...</Text>
                    {currentPhase && (
                      <Text fontSize="sm" color="gray.600">Phase: {currentPhase}</Text>
                    )}
                  </VStack>
                </HStack>
                
                {progress > 0 ? (
                  <Progress value={progress} colorScheme="blue" />
                ) : (
                  <Progress isIndeterminate colorScheme="blue" />
                )}
                
                <Text color="gray.600" fontSize="sm">
                  This may take several minutes. The system is analyzing your requirements and
                  generating comprehensive specifications, code, and documentation.
                </Text>

                {fetching && (
                  <Alert status="info">
                    <AlertIcon />
                    Processing your request... Please do not refresh or navigate away.
                  </Alert>
                )}
                
                {/* Show error controls if there are errors or long processing */}
                {(errorHistory.length > 0 || currentIteration > 1) && (
                  <Alert status="warning">
                    <AlertIcon />
                    <Box flex="1">
                      <AlertTitle>Need Help?</AlertTitle>
                      <AlertDescription>
                        {errorHistory.length > 0 
                          ? `${errorHistory.length} error(s) occurred. You can cancel and try different parameters, or reset completely.`
                          : 'Generation is taking longer than expected. You can cancel or wait for completion.'
                        }
                      </AlertDescription>
                    </Box>
                    <VStack spacing={2}>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => setCurrentStep(STEPS.PARAMETERS)}
                      >
                        Modify Parameters
                      </Button>
                      <Button 
                        size="sm" 
                        colorScheme="red" 
                        onClick={resetForm}
                      >
                        Start Over
                      </Button>
                    </VStack>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>
        )}

        {currentStep === STEPS.QUESTIONS && clarifyingQuestions && (
          <Card>
            <CardHeader>
              <Heading size="md">Clarification Questions</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Additional Information Needed</AlertTitle>
                    <AlertDescription>
                      Please answer the following questions to help generate a more accurate system specification.
                    </AlertDescription>
                  </Box>
                </Alert>

                {clarifyingQuestions.map((question, index) => (
                  <FormControl key={index}>
                    <FormLabel>{question.question}</FormLabel>
                    {question.type === 'text' && (
                      <Textarea
                        placeholder={question.placeholder || "Enter your answer..."}
                        value={questionResponses[question.id] || ''}
                        onChange={(e) => setQuestionResponses(prev => ({
                          ...prev,
                          [question.id]: e.target.value
                        }))}
                      />
                    )}
                    {question.type === 'select' && (
                      <Select
                        value={questionResponses[question.id] || ''}
                        onChange={(e) => setQuestionResponses(prev => ({
                          ...prev,
                          [question.id]: e.target.value
                        }))}
                      >
                        <option value="">Select an option...</option>
                        {question.options.map((option, optIndex) => (
                          <option key={optIndex} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    )}
                    {question.description && (
                      <Text fontSize="sm" color="gray.600" mt={1}>
                        {question.description}
                      </Text>
                    )}
                  </FormControl>
                ))}

                <HStack spacing={4}>
                  <Button 
                    onClick={() => {
                      setCurrentStep(STEPS.GENERATION);
                      setClarifyingQuestions(null);
                      // Continue generation without answers
                      try {
                        generateAIDataSystem({
                          ...formData,
                          continueGeneration: true,
                          currentIteration: currentIteration
                        });
                      } catch (err) {
                        console.error('Skip questions error:', err);
                        toast({
                          title: 'Error',
                          description: 'Failed to continue generation without answers.',
                          status: 'error',
                          duration: 3000,
                          isClosable: true,
                        });
                      }
                    }}
                    variant="outline"
                  >
                    Skip Questions
                  </Button>
                  <Button 
                    colorScheme="blue" 
                    onClick={async () => {
                      setCurrentStep(STEPS.GENERATION);
                      setClarifyingQuestions(null);
                      
                      try {
                        await generateAIDataSystem({
                          ...formData,
                          questionResponses: questionResponses,
                          continueGeneration: true,
                          currentIteration: currentIteration,
                          previousPhase: currentPhase
                        });
                        
                        toast({
                          title: 'Continuing Generation',
                          description: 'Processing your answers and continuing system generation.',
                          status: 'info',
                          duration: 3000,
                          isClosable: true,
                        });
                      } catch (err) {
                        console.error('Continue with answers error:', err);
                        setErrorHistory(prev => [...prev, {
                          timestamp: new Date().toISOString(),
                          error: err.message || 'Failed to continue generation with answers',
                          iteration: currentIteration,
                          phase: 'questions_response'
                        }]);
                        
                        toast({
                          title: 'Generation Error',
                          description: err.message || 'Failed to continue generation with your answers.',
                          status: 'error',
                          duration: 5000,
                          isClosable: true,
                        });
                      }
                    }}
                    isDisabled={Object.keys(questionResponses).length === 0}
                    loadingText="Continuing..."
                    isLoading={fetching && currentStep === STEPS.GENERATION}
                  >
                    Continue with Answers
                  </Button>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        )}

        {currentStep === STEPS.RESULTS && data?.responseType === 'final_result' && (
          <Card>
            <CardHeader>
              <Heading size="md" color="green.600">
                <Icon as={CheckIcon} mr={2} />
                System Generated Successfully!
              </Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Alert status="success">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Generation Complete</AlertTitle>
                    <AlertDescription>
                      Your AI system has been successfully generated with complete specifications,
                      implementation code, and documentation.
                    </AlertDescription>
                  </Box>
                </Alert>

                <HStack spacing={4}>
                  <Button leftIcon={<DownloadIcon />} onClick={downloadSpecifications}>
                    Download Specifications
                  </Button>
                  <Button onClick={resetForm} variant="outline">
                    Generate Another System
                  </Button>
                </HStack>

                <Tabs>
                  <TabList>
                    <Tab>System Overview</Tab>
                    <Tab>User Guide</Tab>
                    <Tab>Technical Details</Tab>
                    <Tab>Next Steps</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel>
                      {data.userVisible && (
                        <Box p={4} bg="gray.50" borderRadius="md">
                          <Code whiteSpace="pre-wrap" fontSize="sm">
                            {JSON.stringify(data.userVisible, null, 2)}
                          </Code>
                        </Box>
                      )}
                    </TabPanel>
                    <TabPanel>
                      {data.specification && (
                        <Box p={4} bg="gray.50" borderRadius="md">
                          <Code whiteSpace="pre-wrap" fontSize="sm">
                            {JSON.stringify(data.specification, null, 2)}
                          </Code>
                        </Box>
                      )}
                    </TabPanel>
                    <TabPanel>
                      {data.implementation && (
                        <Box p={4} bg="gray.50" borderRadius="md">
                          <Code whiteSpace="pre-wrap" fontSize="sm">
                            {JSON.stringify(data.implementation, null, 2)}
                          </Code>
                        </Box>
                      )}
                    </TabPanel>
                    <TabPanel>
                      <VStack spacing={3} align="start">
                        <Text>1. Download and review the specifications</Text>
                        <Text>2. Set up your development environment</Text>
                        <Text>3. Follow the implementation guide</Text>
                        <Text>4. Contact support if you need assistance</Text>
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Error History */}
        {errorHistory.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md" color="red.600">Error History</Heading>
            </CardHeader>
            <CardBody>
              <Accordion allowToggle>
                {errorHistory.map((errorItem, index) => (
                  <AccordionItem key={index}>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <HStack>
                          <Badge colorScheme="red">Error {index + 1}</Badge>
                          <Text>{new Date(errorItem.timestamp).toLocaleString()}</Text>
                        </HStack>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <Text color="red.600">{errorItem.error}</Text>
                      <Text fontSize="sm" color="gray.600" mt={2}>
                        Iteration: {errorItem.iteration}
                      </Text>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardBody>
          </Card>
        )}

        {/* Generation History */}
        {generationHistory.length > 0 && (
          <Card>
            <CardHeader>
              <Heading size="md">Generation History</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                {generationHistory.map((generation, index) => (
                  <Box key={index} p={4} borderWidth={1} borderRadius="md">
                    <HStack justify="space-between">
                      <HStack>
                        <Badge colorScheme="green">Success</Badge>
                        <Text>{new Date(generation.timestamp).toLocaleString()}</Text>
                      </HStack>
                      <Button size="sm" variant="outline" onClick={downloadSpecifications}>
                        Download
                      </Button>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}
      </VStack>

      {/* Confirmation Modal */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm System Generation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="start">
              <Alert status="warning">
                <AlertIcon />
                <Box>
                  <AlertTitle>High-Cost Operation</AlertTitle>
                  <AlertDescription>
                    This will generate a complete AI system and may cost $2-10 in API usage.
                  </AlertDescription>
                </Box>
              </Alert>
              <Text>Are you absolutely sure you want to proceed?</Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onConfirmClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleConfirmGeneration}>
              Yes, Generate System
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Vector Store Creation Modal */}
      <VectorStoreCreationModal
        isOpen={isVectorStoreModalOpen}
        onClose={() => setIsVectorStoreModalOpen(false)}
        onFilesSelected={handleFileUpload}
        selectedFiles={uploadedFiles}
      />
    </Container>
  );
}